import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/offers")({
  component: AdminOffers,
});

function AdminOffers() {
  const qc = useQueryClient();
  const offers = useQuery({ queryKey: ["admin-offers"], queryFn: () => api.adminOffers() });
  const games = useQuery({ queryKey: ["admin-offers-games"], queryFn: () => api.games({ limit: 60 }) });
  const shops = useQuery({ queryKey: ["admin-offers-shops"], queryFn: () => api.adminShops() });
  const [open, setOpen] = useState(false);

  const create = useMutation({
    mutationFn: (body: any) => api.adminCreateOffer(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-offers"] }); setOpen(false); },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.adminDeleteOffer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-offers"] }),
  });

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Offers</h1>
          <p className="text-sm text-muted-foreground">Create real shop offers. Prices in integer cents.</p>
        </div>
        <button onClick={() => setOpen(true)} disabled={!games.data?.data.length || !shops.data?.data.length} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
          <Plus className="h-4 w-4" /> Add offer
        </button>
      </header>

      {(!games.data?.data.length || !shops.data?.data.length) && (
        <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
          You need at least one imported game and one shop before creating offers.
        </div>
      )}

      {offers.isError && <ErrorState message={(offers.error as Error).message} />}
      {offers.isSuccess && offers.data.data.length === 0 && !open && (
        <EmptyState title="No offers yet" description="Add real shop offers to power the comparison." />
      )}

      {open && (
        <OfferForm
          games={games.data?.data ?? []}
          shops={shops.data?.data ?? []}
          onCancel={() => setOpen(false)}
          onSubmit={(b: any) => create.mutate(b)}
          submitting={create.isPending}
          error={create.error as Error | null}
        />
      )}

      {offers.isSuccess && offers.data.data.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Game</th>
                <th className="px-4 py-3">Shop</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.data.data.map((o) => (
                <tr key={o.id} className="border-t border-border/60">
                  <td className="px-4 py-2">{o.game?.title}</td>
                  <td className="px-4 py-2">{o.shop.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{o.region ?? "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{o.stockStatus}</td>
                  <td className="px-4 py-2 text-right">{(o.price / 100).toFixed(2)} {o.currency}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => confirm("Delete offer?") && del.mutate(o.id)} className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OfferForm({ games, shops, onCancel, onSubmit, submitting, error }: any) {
  const [f, setF] = useState({
    gameId: "", shopId: "", price: "", currency: "EUR", region: "Global", platform: "Steam",
    drmType: "Steam Key", edition: "", stockStatus: "in_stock", affiliateUrl: "",
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, price: Math.round(Number(f.price) * 100) }); }} className="rounded-xl border border-border bg-card p-5">
      <h2 className="mb-4 font-display text-lg font-semibold">New offer</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Game *">
          <select required value={f.gameId} onChange={(e) => setF({ ...f, gameId: e.target.value })} className={inputCls}>
            <option value="">Select game…</option>
            {games.map((g: any) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </Field>
        <Field label="Shop *">
          <select required value={f.shopId} onChange={(e) => setF({ ...f, shopId: e.target.value })} className={inputCls}>
            <option value="">Select shop…</option>
            {shops.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Price (decimal, e.g. 24.99) *"><input type="number" step="0.01" min={0} required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={inputCls} /></Field>
        <Field label="Currency"><select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className={inputCls}><option>EUR</option><option>USD</option><option>GBP</option></select></Field>
        <Field label="Platform"><input value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value })} className={inputCls} /></Field>
        <Field label="DRM type"><input value={f.drmType} onChange={(e) => setF({ ...f, drmType: e.target.value })} className={inputCls} /></Field>
        <Field label="Region"><input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className={inputCls} /></Field>
        <Field label="Edition"><input value={f.edition} onChange={(e) => setF({ ...f, edition: e.target.value })} className={inputCls} /></Field>
        <Field label="Stock status">
          <select value={f.stockStatus} onChange={(e) => setF({ ...f, stockStatus: e.target.value })} className={inputCls}>
            <option value="in_stock">In stock</option>
            <option value="out_of_stock">Out of stock</option>
            <option value="preorder">Preorder</option>
            <option value="unknown">Unknown</option>
          </select>
        </Field>
        <Field label="Affiliate URL *"><input type="url" required value={f.affiliateUrl} onChange={(e) => setF({ ...f, affiliateUrl: e.target.value })} className={inputCls} /></Field>
      </div>
      {error && <p className="mt-3 rounded-md bg-destructive/15 px-3 py-2 text-xs text-destructive-foreground">{error.message}</p>}
      <div className="mt-4 flex gap-2">
        <button disabled={submitting} className="rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Create offer</button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border bg-surface px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

const inputCls = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary";
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>;
}
